import toast from 'react-hot-toast';
import { useQuery } from 'react-query';

import { ForumPostOptions, ForumPostsResult, getForumPosts } from '@utils/backend-api/forumPosts';
import {
  Scope,
  SearchParams,
  SearchResponse,
  SortOption,
  searchArticles,
} from '@utils/backend-api/search';
import {
  SubscriberDirectoryParams,
  SubscriberDirectoryResponse,
  getSubscriberDirectory,
} from '@utils/backend-api/subscriber_directory';

export const SEARCH_RESULTS_KEY = 'SEARCH_RESULTS_KEY';

const RESULTS_PER_PAGE = 20;

type SearchResult = SearchResponse | ForumPostsResult | SubscriberDirectoryResponse;

export interface UseSearchResultsParams {
  query: string;
  sort?: SortOption;
  page?: number;
  scope: Scope;
}

export interface UseSearchResultsReturn {
  articleData?: SearchResponse;
  forumData?: ForumPostsResult;
  directoryData?: SubscriberDirectoryResponse;
  isLoading: boolean;
  isFetching: boolean;
}

export default function useSearchResults({
  query,
  sort = SortOption.Recent,
  page = 1,
  scope,
}: UseSearchResultsParams): UseSearchResultsReturn {
  const isEnabled = scope === Scope.Articles || !!query;

  const { data, isLoading, isFetching } = useQuery<SearchResult>(
    [SEARCH_RESULTS_KEY, scope, query, sort, page],
    () => {
      switch (scope) {
        case Scope.Articles: {
          const params: SearchParams = {
            query,
            sort,
            page,
            scope,
          };
          return searchArticles(params);
        }

        case Scope.Forum: {
          const params: ForumPostOptions = {
            onSearchPage: true,
            page,
            perPage: RESULTS_PER_PAGE,
            topic: query.toLowerCase(),
          };
          return getForumPosts(params);
        }

        case Scope.Directory: {
          const params: SubscriberDirectoryParams = {
            ai_query: query,
          };
          return getSubscriberDirectory(params);
        }

        default:
          throw new Error(`Unsupported search scope: ${scope}`);
      }
    },
    {
      // request() rejects with the server's JSON body (e.g. { message }), not an Error instance
      onError: (error: unknown) => {
        const message = (error as { message?: unknown } | null)?.message;
        if (typeof message === 'string' && message) {
          toast.error(message);
        }
      },
      enabled: isEnabled,
      keepPreviousData: false,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  return {
    articleData: scope === Scope.Articles ? (data as SearchResponse) : undefined,
    forumData: scope === Scope.Forum ? (data as ForumPostsResult) : undefined,
    directoryData: scope === Scope.Directory ? (data as SubscriberDirectoryResponse) : undefined,
    isLoading,
    isFetching,
  };
}
